"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import Link from "next/link";

import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import { KATEGORI, MATERI } from "./data";
import TombolUnduhPDF from "./TombolUnduhPDF";

/* ============================================================
   EDUKASI KESEHATAN - /edukasi-kesehatan

   ============================================================
   PERBAIKAN UTAMA: LIGHTBOX PINDAH KE PORTAL

   MASALAHNYA. Header di layout.js posisinya `fixed` dengan z-50.
   Lightbox versi sebelumnya dirender di dalam pohon halaman dengan
   z-100 - angkanya lebih besar, tapi tetap tertutup header. Akibatnya
   baris atas lightbox (judul, penghitung halaman, tombol tutup)
   hilang di balik header, dan pengunjung tidak punya jalan keluar
   yang terlihat selain menekan Esc atau menebak mengklik latar.

   KENAPA MENAIKKAN z-INDEX TIDAK MENOLONG. z-index hanya dibandingkan
   ANTAR SAUDARA di dalam satu konteks penumpukan. Begitu ada leluhur
   yang membuat konteks penumpukan sendiri - dan itu terjadi pada
   elemen ber-transform, ber-filter, ber-opacity kurang dari 1, atau
   ber-backdrop-filter - seluruh isinya terkurung di dalamnya. Anak
   dengan z-9999 sekalipun tidak bisa naik melewati saudara induknya.
   Menaikkan angkanya jadi perlombaan yang tidak pernah selesai.

   PENYELESAIANNYA. Lightbox dirender lewat createPortal ke
   document.body, jadi ia jadi saudara langsung header, bukan cucunya.
   Barulah z-index-nya berlaku sebagaimana mestinya.

   `mounted` diperlukan karena `document` tidak ada saat render di
   peladen. Tanpa penjagaan itu, build gagal dengan "document is not
   defined".

   ============================================================
   TOMBOL KEMBALI, BUKAN HANYA IKON X

   Ikon X di pojok mengandalkan pengunjung tahu artinya. Sekarang ada
   tombol bertuliskan "Kembali ke daftar" lengkap dengan panah -
   terbaca siapa pun, dan target sentuhnya jauh lebih besar. Ikon X
   tetap ada di kanan atas untuk yang sudah terbiasa (disembunyikan di
   ponsel supaya baris atas tidak berdesakan).

   ============================================================
   TIGA HAL YANG MEMBUATNYA JALAN DI SEMUA UKURAN LAYAR

   1. TATA LETAK KOLOM FLEKSIBEL. Baris atas dan bawah `shrink-0`,
      area gambar `flex-1 min-h-0`. Tanpa `min-h-0`, anak flex menolak
      menyusut di bawah tinggi isinya dan gambar mendorong tombol
      bawah keluar layar - persis jenis kerusakan yang hanya muncul di
      layar pendek.

   2. TINGGI PAKAI dvh, BUKAN vh. Di peramban ponsel, bilah alamat
      muncul dan hilang saat digulir; vh mengabaikannya sehingga
      bagian bawah lightbox terpotong. dvh mengikuti tinggi yang
      benar-benar terlihat.

   3. PADDING AMAN iPHONE. env(safe-area-inset-*) menjaga tombol tidak
      tertutup poni layar di atas dan batang beranda di bawah.

   ============================================================
   GESER JARI untuk berpindah halaman di ponsel. Ambangnya 50px supaya
   sentuhan kecil saat menggulir tidak terbaca sebagai perintah pindah.

   ============================================================
   TANPA ZOOM.

   Tombol perbesar-perkecil beserta seluruh mesinnya sudah dicabut
   atas permintaan - bukan sekadar disembunyikan. Menyembunyikan
   tombolnya saja akan menyisakan zoom yang tetap bisa dipicu klik
   ganda dan tuts + / - tanpa satu pun penunjuk di layar, dan itu
   lebih membingungkan daripada tidak ada zoom sama sekali.

   ⚠️ AKIBAT YANG PERLU DIKETAHUI: leaflet dirancang untuk kertas A4
   lipat tiga. Dimuat utuh ke layar ponsel, huruf isinya jadi sekitar
   4px - tidak terbaca. Pengunjung ponsel kini hanya bisa mengenali
   judul dan gambarnya, tidak isinya.

   Kalau nanti itu jadi masalah, jalan paling murah bukan
   mengembalikan zoom, melainkan menambahkan satu tautan "buka gambar
   penuh" yang membuka berkasnya di tab baru - di sana peramban sudah
   menyediakan zoom dan geser bawaannya sendiri, tanpa satu baris kode
   pun dari kita.
   ============================================================ */

/* ============================================================
   WARNA LATAR LIGHTBOX

   Dikumpulkan di sini supaya bisa disetel di satu tempat.

   TIRAI dibuat tembus pandang, bukan bidang plum penuh: isi halaman
   di belakangnya masih terlihat samar, jadi pengunjung tetap tahu ia
   sedang membuka sesuatu DI ATAS daftar, bukan berpindah halaman.
   Blur yang mengerjakan sebagian besar tugasnya - ia meredam detail
   di belakang tanpa perlu menggelapkan banyak.

   Warnanya NETRAL GELAP (biru-abu 15,23,42), bukan plum. Di atas
   leaflet yang warnanya bermacam-macam - merah, hijau, merah muda -
   tirai berwarna ikut mewarnai gambarnya dan mengubah kesan warna
   leafletnya. Netral tidak melakukan itu.

   BILAH SEKARANG TRANSPARAN, jadi bilah atas dan bawah menyatu penuh
   dengan tirai - tidak ada tangga warna, tidak ada garis pemisah.

   Sebelumnya bilah punya lapisan gelap sendiri di atas tirai. Itu
   menjamin teks putih tetap terbaca berapa pun terangnya halaman di
   belakang, tapi konsekuensinya bilah jadi lebih gelap dari bidang di
   tengahnya - dan batas antara keduanya terbaca sebagai garis yang
   tidak disengaja.

   ⚠️ KALAU NANTI TEKS DI BILAH TERASA SULIT DIBACA, itu penyebabnya:
   kini yang menjaga kontras hanya TIRAI. Dua jalan keluarnya - naikkan
   TIRAI sedikit (0,72 -> 0,78), atau kembalikan BILAH ke nilai gelap
   seperti "rgba(15, 23, 42, 0.45)" dan terima garis sambungannya.

   ⚠️ SOAL ANGKA 0,72 PADA TIRAI. Versi sebelumnya 0,52, dan hasilnya
   halaman di belakang masih terbaca penuh - leaflet jadi terlihat
   seperti gambar yang menempel di daftar, bukan penampil yang
   disengaja. Itu justru yang membuatnya terasa belum jadi. 0,72 masih
   jelas tembus pandang: bentuk dan warna halaman di belakang tetap
   terlihat, tapi tidak lagi bersaing dengan leafletnya.

   Kalau terasa terlalu terang atau terlalu gelap, ubah angka terakhir
   pada kedua nilai di bawah. 0 = bening, 1 = pekat. */
/* ============================================================
   LEBAR MAKSIMAL PENAMPIL

   >>> INI ANGKA YANG KAMU SETEL KALAU MAU LEBIH KECIL ATAU BESAR. <<<

   Satuannya piksel. Sebelumnya dipatok lewat kelas max-w-5xl (1024px)
   dan terasa terlalu memenuhi layar. 880 kira-kira selebar leaflet A4
   lipat tiga dibaca dari jarak normal.

   Angka ini dipakai BERSAMAAN oleh tiga bagian: bilah atas, bingkai
   gambar, dan bilah bawah. Itu yang menjaga ketiga tepinya tetap
   segaris - kalau ditulis terpisah, satu perubahan membuatnya meleset.

   Ditulis sebagai style inline, bukan kelas Tailwind, dengan alasan
   yang sama seperti properti lain di komponen ini: kelas pada elemen
   ini sudah berkali-kali gagal ter-generate.

   Di layar yang lebih sempit dari angka ini, w-full yang berlaku -
   jadi di ponsel tidak ada yang berubah.
   ============================================================ */
const LEBAR_MAKS = 880;

const TIRAI = "rgba(15, 23, 42, 0.72)";
const BILAH = "transparent";

/* ============================================================
   TAUTAN INDUK DI BREADCRUMB

   SUDAH true. app/tentang-kami/page.jsx kini ADA - halaman indeks
   berisi kartu About, Gallery, dan Edukasi Kesehatan - jadi "Tentang
   Kami" di breadcrumb boleh jadi tautan.

   Konstanta ini dulu false karena /tentang-kami masih 404: folder itu
   hanya berisi dua sub-folder, about/ dan gallery/, tanpa page.jsx
   untuk induknya sendiri. Breadcrumb yang membawa pengunjung ke
   halaman rusak lebih buruk daripada breadcrumb tanpa tautan - yang
   pertama membuat orang menyimpulkan situsnya rusak. Alasan itu sudah
   tidak berlaku.

   TIDAK DIHAPUS meski nilainya sekarang tetap true. Percabangan di
   bawah menyimpan kedua bentuk - <Link> dan <span> - jadi kalau suatu
   saat /tentang-kami dipindah atau dihapus, mematikan tautannya cukup
   satu kata di sini, bukan menyunting JSX di tengah berkas.

   DUA BERKAS LAIN memakai konstanta ini untuk induk yang sama, dan
   harus ikut diubah supaya ketiganya tidak berbeda perilaku:
     app/tentang-kami/about/AboutContent.jsx
     app/tentang-kami/gallery/GalleryContent.jsx

   Empat berkas layanan memakai konstanta bernama sama untuk induk
   yang BERBEDA, yaitu /layanan - jangan tertukar:
     app/layanan/igd/IgdContent.jsx
     app/layanan/mcu/MCUContent.jsx
     app/layanan/laboratorium/LaboratoriumContent.jsx
     app/layanan/swab-pcr/SwabPcrContent.jsx
   ============================================================ */
const TAUTKAN_INDUK = true;

/* Menerima { id, en } maupun string biasa. Urutan cadangan:
   bahasa aktif -> Bahasa Indonesia -> string kosong. */
function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

export default function EdukasiContent() {
  const { lang, t } = useLanguage();

  const [mounted, setMounted] = useState(false);
  const [kategori, setKategori] = useState(KATEGORI[0]?.key ?? "leaflet");
  const [posisi, setPosisi] = useState(null);
  /* Tinggi header diukur di sini, bukan lewat breakpoint CSS. Angkanya
     dari Header.jsx: h-16 (64px) di ponsel; h-10 utility bar +
     h-[72px] bar utama = 112px mulai md. Sama persis dengan pt di
     pembungkus halaman ini - kalau salah satu diubah, ubah keduanya. */
  const [tinggiHeader, setTinggiHeader] = useState(64);

  const tombolRefs = useRef({});
  const tabRefs = useRef([]);
  const pemicu = useRef(null);
  const kembaliRef = useRef(null);
  const sentuh = useRef(null);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const ukur = () => setTinggiHeader(window.innerWidth >= 768 ? 112 : 64);
    ukur();
    window.addEventListener("resize", ukur);
    return () => window.removeEventListener("resize", ukur);
  }, []);

  const daftar = MATERI.filter((m) => m.kategori === kategori);
  const katAktif = KATEGORI.find((k) => k.key === kategori);
  const rasioKategori = katAktif?.rasio ?? "3 / 2";
  const lebarMinKartu = katAktif?.lebarMin ?? 320;
  const terbuka = posisi !== null;
  const materiAktif = terbuka ? daftar[posisi.materi] : null;
  const halamanAktif = materiAktif?.halaman?.[posisi?.halaman] ?? null;
  const jumlahHalaman = materiAktif?.halaman?.length ?? 0;

  const buka = (iMateri) => {
    pemicu.current = daftar[iMateri]?.id ?? null;
    setPosisi({ materi: iMateri, halaman: 0 });
  };

  /* Fokus dikembalikan ke kartu yang tadi diklik. Tanpa ini, menutup
     lightbox melempar fokus ke awal dokumen dan pengguna keyboard
     harus menekan Tab puluhan kali untuk kembali ke posisinya. */
  const tutup = useCallback(() => {
    setPosisi(null);
    const el = tombolRefs.current[pemicu.current];
    if (el) el.focus();
  }, []);

  /* Panah menyeberang antar materi, bukan berhenti di ujung halaman.
     Dari sisi pengunjung, satu tab adalah satu tumpukan lembar;
     berhenti di halaman terakhir memaksanya menutup lightbox, mencari
     kartu berikutnya, lalu membukanya lagi. */
  const geser = useCallback(
    (arah) => {
        setPosisi((kini) => {
        if (!kini) return kini;

        const rata = daftar.flatMap((m, iM) =>
          m.halaman.map((_, iH) => ({ materi: iM, halaman: iH }))
        );
        if (rata.length === 0) return kini;

        const sekarang = rata.findIndex(
          (x) => x.materi === kini.materi && x.halaman === kini.halaman
        );
        const berikut = (sekarang + arah + rata.length) % rata.length;
        return rata[berikut];
      });
    },
    [daftar]
  );

  useEffect(() => {
    if (!terbuka) return;

    const onKey = (e) => {
      if (e.key === "Escape") tutup();
      if (e.key === "ArrowLeft") geser(-1);
      if (e.key === "ArrowRight") geser(1);
    };

    /* Scroll halaman dikunci. Nilai aslinya disimpan dan dikembalikan,
       bukan dipaksa jadi "auto" - kalau ada style lain yang menyetel
       overflow body, memaksanya akan merusak itu. */
    const asli = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);

    /* Fokus dipindahkan ke tombol kembali. Tanpa ini fokus tertinggal
       di kartu yang ada di belakang lightbox, dan Tab berjalan di
       halaman yang tidak terlihat. */
    const timer = setTimeout(() => kembaliRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = asli;
      document.removeEventListener("keydown", onKey);
      clearTimeout(timer);
    };
  }, [terbuka, tutup, geser]);

  /* Roving tabIndex: panah memindahkan tab aktif sekaligus fokusnya.
     Pengguna keyboard mengharapkan tab berpindah dengan panah, dan
     Tab melompat keluar ke isinya. */
  const onTabKey = (e, i) => {
    const arah = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!arah) return;
    e.preventDefault();

    const berikut = (i + arah + KATEGORI.length) % KATEGORI.length;
    setKategori(KATEGORI[berikut].key);
    tabRefs.current[berikut]?.focus();
  };

  const onSentuhMulai = (e) => {
    sentuh.current = e.touches[0].clientX;
  };

  const onSentuhSelesai = (e) => {
    if (sentuh.current === null) return;
    const jarak = e.changedTouches[0].clientX - sentuh.current;
    sentuh.current = null;
    if (Math.abs(jarak) < 50) return;
    geser(jarak < 0 ? 1 : -1);
  };

  const judulAktif = materiAktif ? pilihTeks(materiAktif.judul, lang) : "";
  const altAktif =
    pilihTeks(halamanAktif?.alt, lang) ||
    t("edukasi.altBawaan", {
      judul: judulAktif,
      nomor: (posisi?.halaman ?? 0) + 1,
      total: jumlahHalaman,
    });

  const lightbox = terbuka && materiAktif && (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={judulAktif}
      onClick={tutup}
      className="flex flex-col backdrop-blur-md"
      style={{
        position: "fixed",
        top: tinggiHeader,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: TIRAI,
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: BILAH, flex: "0 0 auto" }}
      >
        <div style={{ maxWidth: LEBAR_MAKS }} className="mx-auto flex w-full items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3">
          <button
            ref={kembaliRef}
            type="button"
            onClick={tutup}
            className="flex shrink-0 items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] font-semibold text-white/90 transition hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:px-3 sm:text-sm"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4">
              <path d="M19 12H5m0 0 6-6m-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="hidden sm:inline">{t("edukasi.kembali")}</span>
          </button>
          <span aria-hidden="true" className="h-5 w-px shrink-0 bg-white/20" />

          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-white sm:text-sm">
              {judulAktif}
            </p>
            <p className="mt-0.5 text-[11px] tabular-nums text-white/60 sm:text-xs">
              {t("edukasi.posisi", { nomor: posisi.halaman + 1, total: jumlahHalaman })}
            </p>
          </div>

          {/* Urutannya: Unduh, lalu pemisah, lalu Tutup.
             Tutup TETAP paling kanan - posisi itu sudah jadi
             kebiasaan, dan menggesernya membuat orang menutup
             lightbox tanpa sengaja saat mengarah ke unduh. */}
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <TombolUnduhPDF materi={materiAktif} judul={judulAktif} t={t} />

            <span aria-hidden="true" className="hidden h-5 w-px bg-white/20 sm:block" />

            <button
              type="button"
              onClick={tutup}
              aria-label={t("edukasi.tutup")}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-white/80 transition hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-5 w-5">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ---------- GAMBAR ----------
         BINGKAI MENGIKUTI BENTUK GAMBAR, BUKAN SEBALIKNYA.

         Versi sebelumnya memberi bingkai putih berukuran tetap - lebar
         LEBAR_MAKS, tinggi penuh - lalu menaruh gambar di dalamnya
         dengan object-contain. Karena perbandingan sisi leaflet tidak
         pernah sama persis dengan kotak itu, sisa ruangnya muncul
         sebagai PITA PUTIH di atas-bawah atau kiri-kanan. Pita itu
         bagian dari bingkai, bukan bagian dari leaflet - dan justru
         itulah yang membuatnya terlihat seperti kesalahan.

         Sekarang bingkainya adalah gambarnya sendiri: sudut bulat,
         garis tepi, dan bayangan dipasang LANGSUNG pada <img>, lalu
         ukurannya dibatasi dua arah - selebar-lebarnya LEBAR_MAKS,
         setinggi-tingginya ruang yang tersedia. Browser yang memilih
         mana yang lebih dulu tercapai, dan hasilnya tidak pernah
         menyisakan ruang kosong di dalam bingkai.

         DUA CABANG, BUKAN SATU. Mode normal dan mode zoom punya
         kebutuhan yang berlawanan: yang satu harus MUAT seluruhnya,
         yang satu harus MELEBIHI wadahnya supaya ada yang digulir.
         Memaksakan satu elemen melayani keduanya persis itulah yang
         melahirkan pita putih tadi. */}
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onSentuhMulai}
        onTouchEnd={onSentuhSelesai}
        style={{
          /* ============================================================
             INI YANG MEMBUAT GAMBAR MUAT SATU LAYAR.

             Sebelumnya ditulis "min-h-0 flex-1" sebagai kelas Tailwind,
             dan keduanya tidak berlaku. Akibatnya berantai:

             flex-1 hilang -> area gambar tidak mengambil sisa ruang,
             ia hanya setinggi isinya.
             min-h-0 hilang -> dan ini yang menentukan. Anak sebuah flex
             container secara bawaan menolak menyusut lebih kecil dari
             isinya (min-height: auto). Jadi gambar setinggi 1500px
             memaksa wadahnya ikut setinggi 1500px, lalu mendorong
             dirinya melewati tepi bawah layar - persis potongan yang
             terlihat di tangkapan layar.

             minHeight: 0 mencabut penolakan itu. Barulah maxHeight
             "100%" pada gambarnya punya arti, karena wadahnya kini
             benar-benar setinggi ruang yang tersedia dan bukan
             setinggi gambarnya.
             ============================================================ */
          display: "flex",
          flex: "1 1 0%",
          minHeight: 0,
          alignItems: "center",
          justifyContent: "center",
          padding: "16px 12px",
        }}
      >
        {!halamanAktif?.src ? (
          <div className="flex h-full items-center justify-center rounded-xl bg-[var(--c-surface)] px-6 text-sm text-[var(--c-muted)]">
            {t("edukasi.gambarKosong")}
          </div>
        ) : (
          /* MODE NORMAL: gambar yang menentukan ukuran bingkainya.

             <img> biasa, bukan next/image. next/image butuh salah satu
             dari dua hal: `fill` yang mengunci ke ukuran induk - persis
             yang menimbulkan pita putih - atau width dan height yang
             harus diketahui di depan. Ukuran asli 18 berkas leaflet
             tidak diketahui dari kode, dan menebaknya berarti mengganti
             satu masalah dengan masalah lain. */
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            src={halamanAktif.src}
            alt={altAktif}
            draggable={false}
            style={{
              /* DUA BATAS SEKALIGUS, dan yang lebih dulu tercapai yang
                 menang.

                 maxHeight menahan materi POTRET: flyer 9:16 yang hanya
                 dibatasi lebar akan menjulang jauh melewati layar.
                 maxWidth menahan materi MENDATAR: leaflet 3:2 yang
                 hanya dibatasi tinggi akan melebar sampai tepi.

                 Karena keduanya dipasang bersamaan, satu aturan yang
                 sama melayani ketiga bentuk materi tanpa perlu tahu
                 sedang menampilkan yang mana. */
              maxWidth: LEBAR_MAKS,
              maxHeight: "100%",
              boxShadow: "0 1px 2px rgba(15,23,42,0.2), 0 24px 48px -16px rgba(15,23,42,0.45)",
            }}
            className="block h-auto w-auto rounded-xl object-contain ring-1 ring-black/10"
          />
        )}
      </div>


      {/* ---------- BARIS BAWAH ----------
         SATU keterangan saja, bukan dua. Versi sebelumnya menampilkan
         ringkasan DAN judul berdampingan - judulnya sudah ada di bilah
         atas, jadi yang terjadi cuma pengulangan yang membuat barisnya
         terlihat penuh tanpa menambah informasi.

         Titik halaman menggantikan tulisan: pada dua sampai tiga
         halaman, titik lebih cepat dibaca daripada angka, dan ia juga
         berfungsi sebagai tombol lompat. */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: BILAH, flex: "0 0 auto" }}
      >
        <div style={{ maxWidth: LEBAR_MAKS }} className="mx-auto flex w-full items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3">
          <TombolNav arah="kiri" onClick={() => geser(-1)} t={t} />

          <div className="min-w-0 flex-1 text-center">
            <p className="line-clamp-2 text-[12px] leading-snug text-white/70 sm:text-[13px]">
              {pilihTeks(materiAktif.ringkas, lang)}
            </p>

            {jumlahHalaman > 1 && (
              <div className="mt-2 flex justify-center gap-1.5">
                {materiAktif.halaman.map((_, iH) => (
                  <button
                    key={iH}
                    type="button"
                    onClick={() => {
                                        setPosisi((k) => ({ ...k, halaman: iH }));
                    }}
                    aria-label={t("edukasi.posisi", { nomor: iH + 1, total: jumlahHalaman })}
                    aria-current={iH === posisi.halaman}
                    className={`h-1.5 rounded-full transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${iH === posisi.halaman ? "w-6 bg-[var(--c-surface)]" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
                  />
                ))}
              </div>
            )}
          </div>

          <TombolNav arah="kanan" onClick={() => geser(1)} t={t} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div className="mx-auto max-w-[1600px] px-4 py-3 sm:px-6 sm:py-3.5 lg:px-10">
          {/* ---------- BREADCRUMB ----------
             TIGA TINGKAT, bukan dua. "Edukasi Kesehatan" berada di
             dalam dropdown Tentang Kami di header, jadi remah rotinya
             harus menunjukkan jalur yang sama - persis seperti halaman
             About dan Gallery.

             ⚠️ ADA KETIDAKCOCOKAN YANG PERLU KAMU PUTUSKAN. Menu
             menempatkan halaman ini DI BAWAH Tentang Kami, tapi
             alamatnya berdiri sendiri di /edukasi-kesehatan - bukan
             /tentang-kami/edukasi-kesehatan seperti dua saudaranya.

             Untuk pengunjung, remah roti di bawah ini sudah benar:
             itulah jalur yang ia tempuh dari menu. Tapi mesin pencari
             membaca struktur dari ALAMAT, bukan dari remah roti, jadi
             keduanya akan bercerita berbeda.

             Dua jalan menyelaraskannya, dan keduanya sah:
               a. Pindahkan berkasnya ke app/tentang-kami/edukasi-kesehatan/
                  lalu perbarui dua tautan di Header.jsx dan page.jsx.
               b. Atau keluarkan "Edukasi Kesehatan" dari dropdown
                  Tentang Kami di Header.jsx dan jadikan menu tersendiri -
                  isinya memang bukan profil perusahaan.

             Saya tidak memilihkan: yang pertama mengubah alamat yang
             mungkin sudah dibagikan, yang kedua mengubah susunan menu
             yang mungkin sudah disepakati tim marketing. */}
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]">
                  {t("header.nav.home")}
                </Link>
              </li>

              <li className="flex items-center">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
                  <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {TAUTKAN_INDUK ? (
                  <Link href="/tentang-kami" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]">
                    {t("about.eyebrow")}
                  </Link>
                ) : (
                  /* span, bukan Link yang dinonaktifkan. Tautan yang
                     dilumpuhkan tetap bisa di-Tab dan tetap dibacakan
                     sebagai "tautan" oleh pembaca layar - menjanjikan
                     sesuatu yang tidak akan terjadi. */
                  <span className="text-[var(--c-muted)]">{t("about.eyebrow")}</span>
                )}
              </li>

              <li className="flex items-center">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
                  <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {t("edukasi.eyebrow")}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <section className="mx-auto max-w-[1600px] px-4 py-10 sm:px-6 sm:py-14 md:py-16 lg:px-10">

        {/* ---------- KEPALA ----------
           Judul 26px di ponsel, bukan 30px. "Materi Edukasi Pasien"
           pada 30px pecah jadi tiga baris di layar 360px. */}
        {/* Jarak ditulis inline - kelas mb-* di berkas ini tidak
           berlaku, dan tanpa jarak, judul halaman menempel langsung ke
           deretan tab. */}
        <div style={{ marginBottom: 36 }}>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)] sm:mb-3 sm:text-xs sm:tracking-[0.18em]">
            {t("edukasi.eyebrow")}
          </p>

          <h1 className="text-balance text-[26px] font-bold leading-[1.15] tracking-tight text-[var(--c-ink)] sm:text-4xl md:text-5xl">
            {t("edukasi.judul")}
          </h1>

          <p className="mt-3.5 max-w-2xl text-[15px] leading-relaxed text-[var(--c-body)] sm:mt-5 sm:text-base md:text-[17px]">
            {t("edukasi.sub")}
          </p>
        </div>

        {/* ---------- TAB ----------
           Bisa digulir mendatar di layar sempit tanpa memunculkan
           batang gulir - kalau kategorinya bertambah, deretannya tidak
           pecah ke baris kedua. */}
        {/* JARAK BAWAH TAB.

           Ini yang membuat deretan tab menabrak kartu: mb-7 tidak
           berlaku, jadi grid mulai persis di garis bawah tab. Selain
           terlihat sempit, ia juga menghapus batas antara "pemilih
           kategori" dan "isinya" - dua hal yang perannya berbeda dan
           seharusnya terbaca terpisah. */}
        <div className="relative" style={{ marginBottom: 32 }}>
          <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-px bg-slate-200" />

          <div role="tablist" aria-label={t("edukasi.tabAria")} className="relative flex gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-2">
            {KATEGORI.map((kat, i) => {
              const aktif = kat.key === kategori;
              const isi = MATERI.filter((m) => m.kategori === kat.key).length;

              return (
                <button
                  key={kat.key}
                  ref={(el) => (tabRefs.current[i] = el)}
                  type="button"
                  role="tab"
                  id={`tab-${kat.key}`}
                  aria-selected={aktif}
                  aria-controls={`panel-${kat.key}`}
                  tabIndex={aktif ? 0 : -1}
                  onClick={() => setKategori(kat.key)}
                  onKeyDown={(e) => onTabKey(e, i)}
                  className={`relative shrink-0 border-b-2 px-3.5 pb-3 pt-2 text-sm font-bold transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] sm:px-6 sm:text-base ${aktif ? "border-[var(--c-brand)] text-[var(--c-brand)]" : "border-transparent text-[var(--c-muted)] hover:text-[var(--c-ink)]"}`}
                >
                  {t(kat.labelKey)}

                  <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums transition-colors duration-200 ${aktif ? "bg-[var(--c-tint)] text-[var(--c-brand-mid)]" : "bg-[var(--c-line-soft)] text-[var(--c-muted)]"}`}>
                    {isi}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ---------- GRID ----------
           SATU KOLOM DI PONSEL. Dua kolom di layar 360px membuat tiap
           kartu selebar 160px - leafletnya jadi bercak warna tanpa satu
           pun huruf yang terbaca, dan judulnya pecah jadi empat baris. */}
        <div role="tabpanel" id={`panel-${kategori}`} aria-labelledby={`tab-${kategori}`}>
          {daftar.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-[var(--c-brand-line)] bg-[var(--c-tint)] px-5 py-12 text-center sm:px-6 sm:py-14">
              <p className="text-sm font-bold text-[var(--c-ink)] sm:text-base">
                {t("edukasi.kosong")}
              </p>
              <p className="mx-auto mt-2 max-w-sm text-[13px] leading-snug text-[var(--c-body)]">
                {t("edukasi.kosongPetunjuk")}
              </p>
            </div>
          ) : (
            /* GRID TANPA JUMLAH KOLOM YANG DIPATOK.

               repeat(auto-fill, minmax(X, 1fr)) menyuruh browser
               memuat sebanyak mungkin kolom selebar minimal X, lalu
               membagi sisa ruangnya rata. Jumlah kolomnya dihitung
               sendiri dari lebar layar - tidak ada breakpoint yang
               perlu ditebak, dan tidak ada kelas Tailwind yang bisa
               gagal ter-generate.

               Nilai X berbeda per kategori (lihat lebarMin di
               data.js): kartu tegak butuh lebih sempit supaya tidak
               menjulang, kartu mendatar butuh lebih lebar supaya
               isinya terbaca.

               Di ponsel, X yang lebih besar dari lebar layar otomatis
               menghasilkan satu kolom - jadi tidak ada penanganan
               khusus yang perlu ditulis. */
            <ul
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${lebarMinKartu}px), 1fr))`,
                gap: 22,
              }}
            >
              {daftar.map((materi, i) => (
                <li key={materi.id}>
                  <KartuMateri
                    materi={materi}
                    lang={lang}
                    t={t}
                    onOpen={() => buka(i)}
                    rasio={materi.rasio ?? rasioKategori}
                    daftarRef={(el) => (tombolRefs.current[materi.id] = el)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ---------- CATATAN ----------
           JARAK ATAS DITULIS INLINE.

           Sebelumnya memakai mt-10 sm:mt-12, dan kelas itu tidak
           berlaku - kotak catatan menempel langsung ke ringkasan kartu
           di barisan terakhir, sehingga terbaca seperti bagian dari
           kartu itu, bukan keterangan untuk seluruh halaman.

           48px cukup untuk memisahkan tanpa membuatnya terasa
           terlempar sendirian di bawah halaman. Ubah angkanya kalau
           mau lebih longgar. */}
        <p
          className="text-[13px] leading-relaxed text-[var(--c-body)] sm:text-sm"
          style={{
            marginTop: 48,
            borderRadius: 12,
            backgroundColor: "#f1faf5",
            border: "1px solid #bfe3cf",
            padding: "16px 18px",
          }}
        >
          {t("edukasi.catatan")}
        </p>
      </section>
      {mounted && lightbox ? createPortal(lightbox, document.body) : null}

      <Footer />
    </div>
  );
}

function KartuMateri({ materi, lang, t, onOpen, rasio, daftarRef }) {
  const judul = pilihTeks(materi.judul, lang);
  const jumlah = materi.halaman?.length ?? 0;
  const sampul = materi.halaman?.[0];

  return (
    <button
      type="button"
      ref={daftarRef}
      onClick={onOpen}
      aria-label={t("edukasi.buka", { judul })}
      className="group flex h-full w-full flex-col text-left transition-transform duration-300 ease-out hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--c-brand-mid)] motion-reduce:transform-none"
      style={{
        borderRadius: 16,
        overflow: "hidden",
        backgroundColor: "#ffffff",
        boxShadow:
          "0 0 0 1px rgba(203,213,225,0.9), 0 1px 2px rgba(47,4,37,0.06)",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: rasio,
          overflow: "hidden",
          backgroundColor: "#f1faf5",
        }}
      >
        {sampul?.src ? (
          <Image
            src={sampul.src}
            alt=""
            fill
            sizes="(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            className="transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transform-none"

            style={{ objectFit: "contain", objectPosition: "center" }}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              textAlign: "center",
              fontSize: 12,
              fontWeight: 600,
              color: "#15804a",
            }}
          >
            {t("edukasi.gambarKosong")}
          </div>
        )}

        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            right: 8,
            bottom: 8,
            borderRadius: 999,
            backgroundColor: "rgba(47,4,37,0.78)",
            padding: "3px 10px",
            fontSize: 11,
            fontWeight: 600,
            color: "#ffffff",
            fontVariantNumeric: "tabular-nums",
            backdropFilter: "blur(4px)",
          }}
        >
          {jumlah > 1 ? t("edukasi.halaman", { n: jumlah }) : t("edukasi.satuHalaman")}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          padding: "14px 16px 16px",
          borderTop: "1px solid #f1f5f9",
        }}
      >
        <h2 className="text-[15px] font-bold leading-snug text-[var(--c-ink)] transition-colors duration-200 group-hover:text-[var(--c-brand)]">
          {judul}
        </h2>

        <p
          className="mt-1.5 text-[13px] leading-relaxed text-[var(--c-body)]"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {pilihTeks(materi.ringkas, lang)}
        </p>
      </div>
    </button>
  );
}

function TombolNav({ arah, onClick, t }) {
  const kiri = arah === "kiri";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={kiri ? t("edukasi.sebelumnya") : t("edukasi.berikutnya")}
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/25 transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-5 w-5">
        <path d={kiri ? "M19 12H5m0 0 6-6m-6 6 6 6" : "M5 12h14m0 0-6-6m6 6-6 6"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}